import { GLOBAL, ProjectCtsResponse, ReferenceByMAEC } from '@resistapp/common/api-types';
import { L2Targets } from '@resistapp/common/assays';
import { AllProjectSampleTypesGroup } from '@resistapp/common/comparable-sample-groups';
import { SampleSubType, SampleType } from '@resistapp/common/sampling-point-types';
import { hasConsistentBioGroupNumbers, hasDuplicateBioNumbers } from '@resistapp/common/sampling-points';
import {
  FullProject,
  FullSample,
  normalisationToAbundanceMode,
  XAxisMode,
  type SampleStatus,
  type SamplingPoint,
} from '@resistapp/common/types';
import { flattenSamplesByUID, getSampleSortValue } from '@resistapp/common/utils';
import { chain, Dictionary, get, values } from 'lodash';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { buildSampleByIdMap } from '../components/plots/bar-box-map/tooltip-sampling-point-utils';
import {
  createVisualSelectionStateResolver,
  getSamplingPointsWithStatusForSelectedGroup,
  VisualSelectionStateResolver,
} from '../components/plots/bar-box-map/utils';
import { getSmplPntColorBySamplingPointId } from '../components/shared/palettes';
import { buildResearchPlotData, ResearchPlotData } from '../data-utils/plot-data/research-plot-data';
import { useProjectCts } from '../hooks/api';
import { QueryFilters } from '../hooks/use-query-filters/use-query-filters';
import { ResearchAdminFeatures, useResearchAdmin } from '../hooks/use-research-admin';
import { calculateWeightedReferenceMean } from '../utils/reference-line-utils';
import { getCachedSampleChipIds } from '../utils/sample-chip-ids';
import { getExactSelectedSubtypeBucket } from '../utils/sample-subtype-filter-utils';
import { useAssayContext } from './assay-context';
import { useSampleDataContext } from './sample-data-context';

export interface SampleSelectionState {
  ids: number[];
  removeOldSelections: boolean;
}

interface BaseResearch {
  error: Error | null;
  loading: boolean;
  queryFilters: QueryFilters;
  plotData: ResearchPlotData | null;
  project: FullProject | null;
  setGraphAndLegendContainerWidth: (width: number) => void;
  graphAndLegendContainerWidth: number;
  samplesBeingSelected: SampleSelectionState;
  setSamplesBeingSelected: (value: SampleSelectionState) => void;
  sampleStatusById: Record<string, SampleStatus>;
  samplesBySmplPntId: Record<number, Array<FullProject['samplesByUID'][string][number]>>;
  samplingPointsForSelectedGroup: SamplingPoint[];
  samplingPointIdsForSelectedGroup: number[];
  allSamplingPoints: SamplingPoint[];
  focusedSamplesBySmplPntId: Record<number, Array<FullProject['samplesByUID'][string][number]>>;
  adminFeatures: ResearchAdminFeatures;
  sequentialSampleIndexes: Record<number, number>;
  consistentBioGroupNumbering: boolean;
  consistentBioNumbering: boolean;
  referenceData: ReferenceByMAEC | undefined;
  getReferenceLevel: () => [number | null, SampleType | SampleSubType | null];
  tooManySamplesToShow: boolean;
  // Shared lookups for the chart cells and legends, built once per project / selection change
  sampleById: Map<number, FullSample>;
  smplPntColorById: Dictionary<string>;
  getVisualSelectionStatus: VisualSelectionStateResolver;
  // Cq rows by sample id and assay, loaded separately and only while the admin modify mode needs them
  ctsBySampleId: ProjectCtsResponse['ctsBySampleId'] | undefined;
  ctsLoading: boolean;
}

interface ResearchLoading extends BaseResearch {
  loading: true;
  plotData: null;
  project: null;
  againstTime: false;
}

interface ResearchLoaded extends BaseResearch {
  loading: false;
  plotData: ResearchPlotData | null;
  project: FullProject;
  againstTime: boolean;
}

const ResearchContext = createContext<ResearchLoading | ResearchLoaded | undefined>(undefined);

const emptySampleById = new Map<number, FullSample>();
const emptySamplingPoints: SamplingPoint[] = [];
const emptySamplingPointIds: number[] = [];
const emptySequentialSampleIndexes: Record<number, number> = {};
const emptyColorById: Dictionary<string> = {};
const getNoReferenceLevel = () => [null, null] as [null, null];

export function ResearchProvider({ children }: { children: React.ReactNode }) {
  const { data: project, queryFilters, loading, error } = useSampleDataContext();
  const { allGeneGroups, getGroup, assaysLoaded } = useAssayContext();
  const adminFeatures = useResearchAdmin();

  const [graphAndLegendContainerWidth, setGraphAndLegendContainerWidth] = useState(0);
  const [samplesBeingSelected, setSamplesBeingSelectedLocal] = useState<SampleSelectionState>({
    ids: [],
    removeOldSelections: false,
  });
  const sampleRef = useRef<SampleSelectionState>({
    ids: [],
    removeOldSelections: false,
  });
  sampleRef.current = samplesBeingSelected;

  const projectIsValid = project && validateProject(project);
  const samplesByUID = project?.samplesByUID;
  const projectId = project?.id;

  // The admin heatmap highlights and tooltips need the Cq rows, which are too large to embed in the project response
  const ctsQuery = useProjectCts(projectId, { enabled: adminFeatures.isModifyAsAdminActive });
  const ctsBySampleId = ctsQuery.data?.ctsBySampleId;
  const ctsLoading = adminFeatures.isModifyAsAdminActive && ctsQuery.isPending;

  // Build a sample dataset for legend purposes: apply chip filtering but ignore focused sampling point selections so that
  // the legend always lists all sampling points of the chosen sample type group irrespective of individual sample
  // selections/searches.
  const samplesByUIDForLegend = useMemo(() => {
    if (!project) return {};

    const { chips } = queryFilters.filters;
    // If all chips are selected (or chip filtering disabled), return the full dataset
    if (!chips.length || chips.length === project.chips.length) {
      return project.samplesByUID;
    }

    // Otherwise, build a filtered copy that only contains samples belonging to the selected chips.
    // Use getSampleChipIds so that a sample appears under every chip that has any CTS rows.
    const chipIdSet = new Set(chips);
    return chain(project.samplesByUID)
      .mapValues(replicates =>
        replicates.filter(sample => {
          const sampleChipIds = getCachedSampleChipIds(sample);
          return sampleChipIds.some(id => chipIdSet.has(id));
        }),
      )
      .pickBy(replicates => replicates.length > 0)
      .value();
  }, [project, queryFilters.filters]);

  const allSamplingPointsForSelectedGroup = useMemo(
    () =>
      getSamplingPointsWithStatusForSelectedGroup(
        samplesByUIDForLegend,
        queryFilters.filters.selectedSampleTypeGroup,
        project?.id,
      ),
    [samplesByUIDForLegend, queryFilters.filters.selectedSampleTypeGroup, project?.id],
  );
  const exactSelectedSubtypeBucket = useMemo(
    () =>
      getExactSelectedSubtypeBucket(
        queryFilters.filters.selectedSampleTypeGroup,
        queryFilters.filters.selectedSamplingPointIdsOrdered,
        allSamplingPointsForSelectedGroup,
      ),
    [
      queryFilters.filters.selectedSampleTypeGroup,
      queryFilters.filters.selectedSamplingPointIdsOrdered,
      allSamplingPointsForSelectedGroup,
    ],
  );
  const samplingPointsForSelectedGroup = useMemo(() => {
    if (!exactSelectedSubtypeBucket) {
      return allSamplingPointsForSelectedGroup;
    }

    const bucketSamplingPointIds = new Set(exactSelectedSubtypeBucket.samplingPointIds);
    return allSamplingPointsForSelectedGroup.filter(samplingPoint => bucketSamplingPointIds.has(samplingPoint.id));
  }, [allSamplingPointsForSelectedGroup, exactSelectedSubtypeBucket]);
  const samplingPointIdsForSelectedGroup = useMemo(
    () => samplingPointsForSelectedGroup.map(point => point.id),
    [samplingPointsForSelectedGroup],
  );
  const sampleStatusById = useMemo(
    () =>
      chain(samplingPointsForSelectedGroup)
        .keyBy(point => `${point.id}`)
        .mapValues(point => point.sampleStatus)
        .value(),
    [samplingPointsForSelectedGroup],
  );

  const selectedSamplingPointIds = queryFilters.filters.selectedSamplingPointIdsOrdered;
  const toggleSamplingPointStable = queryFilters.toggleSamplingPointStable;
  // Clean up non-existent sampling points from URL
  useEffect(() => {
    if (!samplingPointsForSelectedGroup.length || !selectedSamplingPointIds.length) {
      return;
    }

    // Clean up if there are any selected sampling points to ensure they exist abd are if expected type
    const samplingPointIds = new Set(samplingPointsForSelectedGroup.map(point => point.id));
    const validSamplingPoints = selectedSamplingPointIds.filter(id => samplingPointIds.has(id));
    // Only update if we found invalid sampling points
    if (validSamplingPoints.length !== selectedSamplingPointIds.length) {
      toggleSamplingPointStable(undefined, true);
    }
  }, [selectedSamplingPointIds, toggleSamplingPointStable, samplingPointsForSelectedGroup]);

  // Visual selection status is needed by every chart cell and legend label, so resolve it once per selection change
  const getVisualSelectionStatus = useMemo(
    () =>
      createVisualSelectionStateResolver(
        selectedSamplingPointIds,
        samplesBeingSelected,
        samplingPointIdsForSelectedGroup,
      ),
    [selectedSamplingPointIds, samplesBeingSelected, samplingPointIdsForSelectedGroup],
  );

  const consistentBioGroupNumbering = useMemo(() => {
    return samplesByUID ? hasConsistentBioGroupNumbers(samplesByUID) : true;
  }, [samplesByUID]);
  const consistentBioNumbering = useMemo(() => {
    return samplesByUID ? !hasDuplicateBioNumbers(samplesByUID) : true;
  }, [samplesByUID]);

  // PREPARE RESEARCH PLOT DATA
  const plotData = useMemo(() => {
    // TODO this is heavy and should be run only once on load: when filters change.
    // This would be to avoid double runing it first on project load, and then immediatelly again
    // because filters.selectedSamplingPointNamesOrdered depends on the project.
    return projectIsValid && assaysLoaded
      ? buildResearchPlotData(project, queryFilters.filters, allGeneGroups, getGroup)
      : null;
  }, [projectIsValid, assaysLoaded, project, queryFilters.filters, allGeneGroups, getGroup]);

  // ORDINAL DATA SORTING
  const sequentialSampleIndexes = useMemo(() => {
    if (!samplesByUID || projectId === undefined) return emptySequentialSampleIndexes;

    const allSamples = flattenSamplesByUID(samplesByUID);
    const sortedSamples = [...allSamples].sort(
      (a, b) => getSampleSortValue(a, projectId) - getSampleSortValue(b, projectId),
    );

    const indexMap: Record<number, number> = {};
    sortedSamples.forEach((sample, index) => {
      indexMap[sample.id] = index + 1;
    });

    return indexMap;
  }, [samplesByUID, projectId]);

  const samplesBySmplPntId = useMemo(
    () =>
      chain(samplesByUID || {})
        .values()
        .flatten()
        .groupBy(sample => sample.samplingPoint.id)
        .mapValues(samples => samples)
        .value(),
    [samplesByUID],
  );

  const sampleById = useMemo(() => (samplesByUID ? buildSampleByIdMap(samplesByUID) : emptySampleById), [samplesByUID]);

  const smplPntColorById = useMemo(
    () => (samplesByUID ? getSmplPntColorBySamplingPointId(samplesByUID, projectId) : emptyColorById),
    [samplesByUID, projectId],
  );

  const tooManySamplesToShow = useMemo(() => {
    if (!plotData || !graphAndLegendContainerWidth) return false;
    const count = plotData.boxData?.length || 0;
    if (!count) return false;
    const showBioRepIndicator = queryFilters.filters.xAxisMode === XAxisMode.ORDINAL_INDIVIDUAL;
    const thresholdPerSample = showBioRepIndicator ? 34 : 24;
    return graphAndLegendContainerWidth / count < thresholdPerSample;
  }, [plotData, graphAndLegendContainerWidth, queryFilters.filters.xAxisMode]);

  // Prevent repeatedly auto-selecting the first chip when the user explicitly cleared the selection.
  const hasAutoSelectedChipRef = useRef(false);

  const setSamplesBeingSelected = useCallback((newSelection: SampleSelectionState) => {
    if (newSelection.ids.length === 0) {
      setSamplesBeingSelectedLocal({ ids: [], removeOldSelections: newSelection.removeOldSelections });
    } else {
      setSamplesBeingSelectedLocal(newSelection);
    }
  }, []);

  useEffect(() => {
    if (hasAutoSelectedChipRef.current) return;
    if (!project || project.chips.length <= 1 || !tooManySamplesToShow) return;

    const allSmplPntSelected =
      queryFilters.filters.selectedSampleTypeGroup === AllProjectSampleTypesGroup.ALL_PROJECT_SAMPLING_POINTS &&
      queryFilters.filters.selectedSamplingPointIdsOrdered.length === Object.keys(samplesBySmplPntId).length;

    const allChipsSelected = queryFilters.filters.chips.length === project.chips.length;

    if (allSmplPntSelected && allChipsSelected) {
      queryFilters.setChipStable(project.chips[0].id);
      hasAutoSelectedChipRef.current = true;
    }
  }, [project, tooManySamplesToShow, samplesBySmplPntId, queryFilters]);

  const focusedByUID = project?.focusedByUID;
  const focusedSamplesBySmplPntId = useMemo(
    () =>
      chain(focusedByUID || samplesByUID || {})
        .values()
        .flatten()
        .groupBy(sample => sample.samplingPoint.id)
        .mapValues(samples => samples)
        .value(),
    [focusedByUID, samplesByUID],
  );

  // REFERENCE LEVEL GETTER
  const { selectedTargets, selectedSamplingPointIdsOrdered } = queryFilters.filters;
  const getReferenceLevel = useCallback((): [number | null, SampleType | SampleSubType | null] => {
    // Early exit if reference data or plot data isn't available
    if (!project?.researchReferencesByMAEC || !plotData) {
      return [null, null];
    }

    const { normalisationMode } = plotData;
    const referenceMetric = normalisationToAbundanceMode(normalisationMode);
    if (referenceMetric === null) {
      return [null, null];
    }

    // Determine sample type or subtype; return null if multiple
    const environmentTypes = chain(selectedSamplingPointIdsOrdered)
      .map(envId => {
        const samples = get(focusedSamplesBySmplPntId, envId, []);
        return samples.length > 0 ? samples[0].samplingPoint.type : null;
      })
      .filter(type => !!type)
      .uniq()
      .value();
    const environmentSubtypes = chain(selectedSamplingPointIdsOrdered)
      .map(envId => {
        const samples = get(focusedSamplesBySmplPntId, envId, []);
        return samples.length > 0 ? samples[0].samplingPoint.subtype : null;
      })
      .filter(type => !!type)
      .uniq()
      .compact()
      .value();
    if (environmentTypes.length !== 1) {
      return [null, null];
    }
    const environmentTypeOrSubtype = environmentSubtypes.length === 1 ? environmentSubtypes[0] : environmentTypes[0];

    // Determine which gene grouping to use
    const l2TargetSet = new Set(values(L2Targets));
    const selectedL2Targets = selectedTargets.filter(group => l2TargetSet.has(group as L2Targets)) as L2Targets[];

    // Single target - use direct lookup
    if (selectedL2Targets.length === 1) {
      const refLevel = get(
        project.researchReferencesByMAEC,
        [referenceMetric, selectedL2Targets[0], environmentTypeOrSubtype, GLOBAL, 'mean'],
        null,
      ) as number | null;
      return [refLevel, environmentTypeOrSubtype];
    }

    // Multiple targets - use weighted mean calculation
    if (selectedL2Targets.length > 1) {
      const firstFocusedSample: FullSample | undefined = values(project.focusedByUID || project.samplesByUID)[0]?.[0];

      // Use all analysed genes to weight the antibiotics, instead of only detected genes.
      // This makes the reference line deviate slightly from the mean of the a sample, even if the reference level is calculated from only that sample.
      // Using the detected genes in the focused samples would make the reference level flucturate when browsing between samples even within the same sampling point subtype.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const selectedAssays: string[] = firstFocusedSample?.abundances.map(abundance => abundance.assay) || [];

      // You can uncomment this and limit reference calculation to 1 sample (per subtype), to verify that reference levels
      // align with box plot sample mean calculation.
      // const selectedAssays: string[] = firstFocusedSample
      //   ? firstFocusedSample.abundances
      //       .filter(abundance => getNormalisedValue(abundance, normalisationMode) != null)
      //       .map(abundance => abundance.assay)
      //   : [];

      const refLevel = calculateWeightedReferenceMean(
        project.researchReferencesByMAEC,
        referenceMetric,
        selectedL2Targets,
        environmentTypeOrSubtype,
        selectedAssays,
        getGroup,
      );
      return [refLevel, environmentTypeOrSubtype];
    }

    return [null, null];
  }, [project, plotData, selectedTargets, selectedSamplingPointIdsOrdered, focusedSamplesBySmplPntId, getGroup]);

  const loadedProject = projectIsValid && !loading && assaysLoaded ? project : null;

  // The context value is memoized so that the many chart cells subscribing to it only re-render when it changes
  const contextData = useMemo<ResearchLoading | ResearchLoaded>(() => {
    if (loadedProject) {
      return {
        loading: false as const,
        plotData,
        project: loadedProject,
        queryFilters,
        error,
        setGraphAndLegendContainerWidth,
        graphAndLegendContainerWidth,
        samplesBeingSelected,
        setSamplesBeingSelected,
        againstTime: plotData?.againstTime || false,
        sampleStatusById,
        samplesBySmplPntId,
        focusedSamplesBySmplPntId,
        allSamplingPoints: samplingPointsForSelectedGroup,
        samplingPointsForSelectedGroup,
        samplingPointIdsForSelectedGroup,
        adminFeatures,
        sequentialSampleIndexes,
        consistentBioGroupNumbering,
        consistentBioNumbering,
        referenceData: loadedProject.researchReferencesByMAEC,
        getReferenceLevel,
        tooManySamplesToShow,
        sampleById,
        smplPntColorById,
        getVisualSelectionStatus,
        ctsBySampleId,
        ctsLoading,
      };
    }
    return {
      loading: true as const,
      plotData: null,
      project: null,
      queryFilters,
      error,
      setGraphAndLegendContainerWidth,
      graphAndLegendContainerWidth,
      samplesBeingSelected,
      setSamplesBeingSelected,
      againstTime: false as const,
      sampleStatusById,
      samplesBySmplPntId,
      focusedSamplesBySmplPntId,
      samplingPointsForSelectedGroup: emptySamplingPoints,
      samplingPointIdsForSelectedGroup: emptySamplingPointIds,
      allSamplingPoints: emptySamplingPoints,
      adminFeatures,
      sequentialSampleIndexes: emptySequentialSampleIndexes,
      consistentBioGroupNumbering: true,
      consistentBioNumbering: true,
      referenceData: undefined,
      getReferenceLevel: getNoReferenceLevel,
      tooManySamplesToShow: false,
      sampleById: emptySampleById,
      smplPntColorById: emptyColorById,
      getVisualSelectionStatus,
      ctsBySampleId: undefined,
      ctsLoading: false,
    };
  }, [
    loadedProject,
    plotData,
    queryFilters,
    error,
    graphAndLegendContainerWidth,
    samplesBeingSelected,
    setSamplesBeingSelected,
    sampleStatusById,
    samplesBySmplPntId,
    focusedSamplesBySmplPntId,
    samplingPointsForSelectedGroup,
    samplingPointIdsForSelectedGroup,
    adminFeatures,
    sequentialSampleIndexes,
    consistentBioGroupNumbering,
    consistentBioNumbering,
    getReferenceLevel,
    tooManySamplesToShow,
    sampleById,
    smplPntColorById,
    getVisualSelectionStatus,
    ctsBySampleId,
    ctsLoading,
  ]);

  return <ResearchContext.Provider value={contextData}>{children}</ResearchContext.Provider>;
}

export function useResearchContext() {
  const context = useContext(ResearchContext);

  if (!context) {
    throw new Error('useResearchContext must be used within a ResearchProvider');
  }

  return context;
}

/**
 * Validates the structure and content of the sampling data.
 * @param {FullProject | null} project - The sampling data to validate.
 * @returns {boolean} - Returns true if the sampling data is valid, otherwise false.
 */
function validateProject(project: Partial<FullProject>): project is FullProject {
  // Example validation: Ensure that the sampling has necessary properties
  const requiredProperties = ['samplesByUID', 'focusedByUID'];
  const hasAllProperties = requiredProperties.every(prop => prop in project);

  if (!hasAllProperties) {
    return false;
  }

  // Further validations can be added here based on project requirements
  return true;
}
